import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { fnGetDataSet, fnGetTableById, fnSave } from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getInitFormData,
  getconfigData,
} from "@shared/utils/CommonUtils";
import { fnDrilldown } from "@shared/utils/DrilldownUtils";
import { dateToString } from "@shared/utils/DateUtils";
import { global } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnForm, { ValueChangedEvent } from "@widgets/SnForm";
import SnButton from "@shared/components/SnButton";
import SN_TRA_IBL_REG_001_POP_001 from "./SN_TRA_IBL_REG_001_POP_001";
import SN_TRA_IBL_REG_001_POP_002 from "./SN_TRA_IBL_REG_001_POP_002";
import SN_TRA_IBL_REG_001_POP_003 from "./SN_TRA_IBL_REG_001_POP_003";

interface Props {
  Menuprops: menuInfo;
}

// BL등록(BL Notice)
const SN_TRA_IBL_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });
  const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    freeform1: React.createRef(),
  });
  const poTypeRef = useRef<any[]>([]);

  const [columns, setColums] = useState<any>({ grid1: [] });
  const [dataSources, setDataSources] = useState<any>({ grid1: [] });
  const [formControls, setFormControls] = useState<any>({ freeform1: [] });
  const [formDatas, setFormDatas] = useState<any>({ freeform1: null });
  const [fgTrns, setFgTrns] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setColums({ grid1: rtn.grid.grid2 });
        setFormControls({ freeform1: rtn.form.freeform1 });

        const poTypes = await fnGetTableById({
          id: "potype",
          params: { cd_firm: userInfo.cd_firm },
        });
        poTypeRef.current = Array.isArray(poTypes) ? poTypes : [];
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const resetAll = () => {
    formRefs.current["freeform1"].current.resetUpdate();
    gridRefs.current["grid1"].current?.deleteAllRows();
    gridRefs.current["grid1"].current.resetUpdate();
  };

  const onSearch = async () => {
    const modifiedRowsH = formRefs.current["freeform1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid1"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
        return;
      }
      resetAll();
    }
    try {
      await search_popup();
    } catch (error) {}
  };

  const getPopupInitialData = () => {
    const formData =
      formRefs.current["freeform1"].current?.getFormdata?.() ?? {};
    return {
      cd_cust: formData.cd_cust || "",
      nm_cust: formData.nm_cust || "",
      nm_cust_sort: formData.nm_cust || "",
    };
  };

  const search_popup = async () => {
    const component = (
      <SN_TRA_IBL_REG_001_POP_001 selectedData={getPopupInitialData()} />
    );
    const result = await showPopupModal({
      title: "B/L도움",
      children: component,
    });
    if (!result || result === "close") return;

    const headerResult = result[0];
    const params = {
      id: "sn_tra_ibl_reg_001_01",
      params: { no_req: headerResult[0].no_req },
    };
    const data: any = await fnGetDataSet(params);
    setFormDatas({ freeform1: data[0]?.[0] ?? null });
    setDataSources({ grid1: data[1] || [] });
    setFgTrns(String(data[0]?.[0]?.fg_trns || ""));

    formRefs.current["freeform1"].current.resetUpdate();
    gridRefs.current["grid1"].current.resetUpdate();
  };

  const onInsert = async () => {
    try {
      const modifiedRowsH = formRefs.current["freeform1"].current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정중인 건이 있습니다. 저장하지 않고 계속 하시겠습니까?"
        );
        if (!confirm) return;
      }
      resetAll();

      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        cd_biz_rcv: userInfo.cd_biz,
        cd_dept: userInfo.cd_dept,
        nm_dept: userInfo.nm_dept,
        cd_gp: userInfo.cd_gp,
        nm_gp: userInfo.nm_gp,
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        dt_req: today,
        dt_rcv_p: today,
        dt_pay_p: today,
        dt_inv: today,
        st_req: "F",
        dy_pay: 0,
        rt_exch: 1,
        yn_autoprocess: "N",
        yn_autorcv: "N",
        cd_exch: "000",
        fg_trns: "5",
        cd_io: "",
        fg_io: "1",
        cd_wh: "",
      };
      formRefs.current["freeform1"].current.addRow(newRow);
      setFgTrns(newRow.fg_trns);
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onDelete = async () => {
    if (!formRefs.current["freeform1"].current.checkDataExist()) return;

    // 필터로 숨겨진 행까지 검사
    const allRows: any[] = [];
    gridRefs.current["grid1"].current.forEachNode((node: any) =>
      allRows.push(node.data)
    );
    if (await isProgressed(allRows)) {
      await showDialog("alter", "진행된 건입니다.");
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid1"].current?.deleteAllRows();
    formRefs.current["freeform1"].current.deleteForm();
    setFgTrns("");

    const realDeletedRowsGrid =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    const realDeletedRowsForm =
      formRefs.current["freeform1"].current.getChange().delete ?? [];
    if (
      Array.isArray(realDeletedRowsGrid) &&
      realDeletedRowsGrid.length < 1 &&
      Array.isArray(realDeletedRowsForm) &&
      realDeletedRowsForm.length < 1
    ) {
      gridRefs.current["grid1"].current.resetUpdate();
      formRefs.current["freeform1"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      return gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {
      return null;
    }
  };

  const onBeforeAddRow = () => {
    try {
      const formRef = formRefs.current["freeform1"].current;
      if (!formRef.checkDataExist()) {
        showDialog("alter", "상단 행 먼저 추가하시기 바랍니다");
        return false;
      }
      const formData = formRef.getFormdata();
      const today = dateToString(new Date());
      const noLine =
        (gridRefs.current["grid1"].current.getMaxValue("no_line") || 0) + 1;
      return {
        cd_firm: userInfo.cd_firm,
        no_req: formData?.no_req,
        st_req: formData?.st_req,
        dt_dlv: today,
        yn_confirm: "N",
        yn_qc: "N",
        rt_exch: "0000",
        no_line: noLine,
        no_line_po: 0,
        no_line_lc: 0,
        cd_wh: formData?.cd_wh ?? "",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDeleteRow = async () => {
    const grid = gridRefs.current["grid1"].current;
    const selectedRows = grid?.getSelectedRows() ?? [];
    if (selectedRows.length === 0) return;

    if (selectedRows.length >= grid.getRowCount()) {
      await showDialog(
        "alter",
        "상세내역만 전체 삭제할 수 없습니다.\n상단도 함께 삭제하세요."
      );
      return;
    }

    if (await isProgressed(selectedRows)) {
      await showDialog("alter", "진행된 건입니다.");
      return;
    }

    grid.deleteSelectedRows();
    onAfterSetFooter();
  };

  // 입고량 있으면 삭제 불가, 통관량은 PUR0103 = Y 일 때만 삭제 허용
  const isProgressed = async (rows: any[]) => {
    if (rows.some((r) => Number(r.qt_rcv) > 0)) return true;
    if (!rows.some((r) => Number(r.qt_ip) > 0)) return false;
    const config = await getconfigData("PUR0103");
    return config[0]?.dc_ctrl !== "Y";
  };

  const onSave = async () => {
    try {
      formRefs.current["freeform1"].current.stopEditing?.();
      gridRefs.current["grid1"].current.stopEditing();

      if (!formRefs.current["freeform1"].current.requiredValuesCheck())
        return false;
      if (!gridRefs.current["grid1"].current.requiredValuesCheck())
        return false;

      const headerChanges = formRefs.current["freeform1"].current.getChange();
      const lineChanges = gridRefs.current["grid1"].current.getChange();
      if (
        headerChanges.modifiedRowcount === 0 &&
        lineChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const headerInserts = headerChanges.insert ?? [];
      const lineInserts = lineChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      const headerRows = [
        ...(headerChanges.insert ?? []),
        ...(headerChanges.update ?? []),
      ];
      if (headerRows.some((r: any) => Number(r.dy_pay) > 999)) {
        await showDialog("alter", "결제기간은 999일을 초과할 수 없습니다.");
        return false;
      }

      if (headerInserts.length > 0 && !headerInserts[0].no_req) {
        const noSlip = await applySlip({
          cd_column: "no_req",
          cd_module: Menuprops.cd_module,
          no_line: 6,
          cd_prefix: "IBL",
          dt: headerInserts[0].dt_req,
          girdrefs: [gridRefs.current["grid1"]],
          formrefs: [formRefs.current["freeform1"]],
          cd_menu: Menuprops.cd_menu,
        });
        if (!noSlip) return false;
      }

      const saveData = {
        pur_rcvreqh: formRefs.current["freeform1"].current.getChange(),
        pur_rcvreql: gridRefs.current["grid1"].current.getChange(),
      };
      const ok = await fnSave("SN_TRA_IBL_REG_001_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid1"].current.resetUpdate(true);
        formRefs.current["freeform1"].current.resetUpdate(true);
        return false;
      }

      formRefs.current["freeform1"].current.resetUpdate();
      gridRefs.current["grid1"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      formRefs.current["freeform1"].current.resetUpdate(true);
      return false;
    }
  };

  const applyCdIoFromPo = (cdPo?: string) => {
    if (!cdPo) return;
    const cdIo = poTypeRef.current.find((po) => po.cd_po === cdPo)?.cd_io;
    if (cdIo) {
      formRefs.current["freeform1"].current.setDataValue(
        "cd_io",
        cdIo,
        "noEvent"
      );
    }
  };

  const onGridBtnsClick = async (id: string) => {
    const formRef = formRefs.current["freeform1"].current;
    if (formRef.getChange().insert.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    const isLc = id === "btnApplyLC";
    const formData = formRef.getFormdata();
    const selectedData = {
      cd_biz: formData.cd_biz,
      fg_trns: formData.fg_trns,
      cd_cust: formData.cd_cust,
      nm_cust: formData.nm_cust,
      nm_cust_sort: formData.nm_cust_sort,
    };
    const result = await showPopupModal({
      title: isLc ? "L/C적용" : "발주적용",
      children: isLc ? (
        <SN_TRA_IBL_REG_001_POP_002 selectedData={selectedData} />
      ) : (
        <SN_TRA_IBL_REG_001_POP_003 selectedData={selectedData} />
      ),
      width: "80%",
    });
    if (!result || result === "close") return;

    const header = result[0]?.[0];
    const lineResult: any[] = result[1] ?? [];

    if (header) {
      for (const key in header) {
        if (!isLc && key === "fg_trns") continue;
        formRef.setDataValue(key, header[key], "noEvent");
      }
      if (isLc && header.fg_trns !== undefined) {
        setFgTrns(String(header.fg_trns || ""));
      }
      if (!isLc) applyCdIoFromPo(header.cd_po);
    }

    const noPo = header?.no_po ?? lineResult[0]?.no_po;
    if (!isLc && noPo) formRef.setDataValue("no_po", noPo, "noEvent");

    const today = dateToString(new Date());
    for (const item of lineResult) {
      const rowNode = onAddRow();
      if (!rowNode) continue;
      for (const key in item) {
        // 원천 라인번호는 no_line_lc(L/C) / no_line_po(발주)로, no_line은 행추가 채번값 유지
        if (key === "no_line") continue;
        rowNode.setDataValue(key, item[key], "noEvent");
      }
      rowNode.setDataValue(
        isLc ? "no_line_lc" : "no_line_po",
        item.no_line,
        "noEvent"
      );
      rowNode.setDataValue("dt_dlv", today, "noEvent");
    }
    await CalAmBulk(
      formRefs.current["freeform1"],
      gridRefs.current["grid1"],
      "rt_exch"
    );
  };

  const onValueChanged = async (event: ValueChangedEvent) => {
    if (!event) return;
    if (event.colId === "fg_trns") {
      setFgTrns(String(event.newValue || ""));
    }
    if (event.colId === "cd_po") {
      applyCdIoFromPo(String(event.newValue || ""));
    }
    if (event.colId === "cd_wh") {
      gridRefs.current["grid1"].current?.forEachNode((node: any) => {
        node.setDataValue("cd_wh", event.newValue);
      });
    }
    await CalAmBulk(
      formRefs.current["freeform1"],
      gridRefs.current["grid1"],
      event.colId
    );
  };

  const onCellValueChangedL = async (event: CellValueChangedEvent) => {
    if (!event) return;
    await CalAm(
      formRefs.current["freeform1"],
      gridRefs.current["grid1"],
      event.colId
    );
  };

  const onAfterSetFooter = async () => {
    const footerData = gridRefs.current["grid1"].current.getFooterData();
    formRefs.current["freeform1"].current.setDataValue(
      "am",
      footerData.am,
      "noEvent"
    );
    formRefs.current["freeform1"].current.setDataValue(
      "am_k",
      footerData.am_k,
      "noEvent"
    );
    formRefs.current["freeform1"].current.setDataValue(
      "am_vat",
      footerData.am_vat,
      "noEvent"
    );
    formRefs.current["freeform1"].current.setDataValue(
      "am_supply",
      footerData.am_supply,
      "noEvent"
    );
  };

  const onContextMenu = async (params: any) => {
    const targetMenu = params?.menu; // 부대비용등록 = SN_TRA_IBL_REG_003
    if (!targetMenu) return;
    const noReq =
      formRefs.current["freeform1"].current?.getFormdata?.()?.no_req;
    if (!noReq) {
      showDialog("alter", "저장된 B/L 건을 선택 후 처리하세요.");
      return;
    }
    await fnDrilldown({
      cd_menu: targetMenu,
      no_docu: noReq,
      no_line: "",
      fg_map: "002",
    });
  };

  const forms = useMemo(
    () => ({
      freeform1: {
        controls: formControls.freeform1,
        data: formDatas.freeform1,
        etcProps: {},
        events: {
          onValueChanged: onValueChanged,
          onContextMenu: onContextMenu,
        },
      },
    }),
    [formControls, formDatas]
  );

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onCellValueChanged: onCellValueChangedL,
          onBtnDeleteClick: onDeleteRow,
          onBeforeAddRow: onBeforeAddRow,
          onAfterSetFooter: onAfterSetFooter,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="53.5%">
          <SnForm
            ref={formRefs.current["freeform1"]}
            data={forms.freeform1.data}
            controls={forms.freeform1.controls}
            etcProps={forms.freeform1.etcProps}
            events={forms.freeform1.events}
          ></SnForm>
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="44.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              isEditable: true,
              rowSelection: "multiple",
              title: "BL",
            }}
            events={grids.grid1.events}
            header={
              <div>
                {(fgTrns === "2" || fgTrns === "4") && (
                  <SnButton
                    text={"L/C적용"}
                    onClick={() => {
                      onGridBtnsClick("btnApplyLC");
                    }}
                  />
                )}
                {(fgTrns === "2" || fgTrns === "5") && (
                  <SnButton
                    text={"발주적용"}
                    onClick={() => {
                      onGridBtnsClick("btnApplyOrder");
                    }}
                  />
                )}
              </div>
            }
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_TRA_IBL_REG_001;
